import StatusScreen from "@/components/status-screen";
import {
  useGetProviderBreakdown,
  useGetTestResult,
} from "@/hooks/use-inboxplacement";
import { PlacementType } from "@/types/inboxPlacements.types";
import { InfoIcon, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PlacementContext } from "./placement-context";
import { calculateSummaryStats, SummaryStats } from "./placement-utils";

export type PlacementState = {
  isPrinting: boolean;
  params: {
    email: string;
    type: PlacementType;
    testId: string;
  };
};

const getRefetchTimesKey = (testId: string) => `IPR-refetch-times-${testId}`;

// Add constants at the top of the file
const REFETCH_INTERVAL_MS = 10000; // 10 seconds
const MAX_REFETCH_COUNT = 45;
const MAX_REPORT_AGE_MINUTES = 15;

// Helper functions for session storage
const SessionStorageManager = {
  getRefetchCount(testId: string): number {
    try {
      const key = getRefetchTimesKey(testId);
      const value = sessionStorage.getItem(key);
      const count = value ? parseInt(value, 10) : 0;
      return isNaN(count) ? 0 : count;
    } catch {
      return 0;
    }
  },

  setRefetchCount(testId: string, count: number): void {
    try {
      const key = getRefetchTimesKey(testId);
      sessionStorage.setItem(key, count.toString());
    } catch (error) {
      console.error("Failed to set refetch count:", error);
    }
  },

  clearRefetchCount(testId: string): void {
    try {
      const key = getRefetchTimesKey(testId);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error("Failed to clear refetch count:", error);
    }
  },
};

export const PlacementProvider = ({
  children,
  email,
  type,
  testId,
}: {
  children: React.ReactNode;
  email: string;
  type: PlacementType;
  testId: string;
}) => {
  const [state, setState] = useState<PlacementState>({
    isPrinting: false,
    params: {
      email,
      type,
      testId,
    },
  });

  const {
    data,
    isLoading: isLoadingInboxPlacementReport,
    refetch: refetchInboxPlacementReport,
  } = useGetTestResult(email, type, testId);

  const {
    data: providerBreakdown,
    isLoading: providerBreakdownLoading,
    refetch: refetchProviderBreakdown,
  } = useGetProviderBreakdown(
    email,
    testId || "" // This will be an empty string until data is loaded
  );

  const summary = useMemo(
    () => calculateSummaryStats(providerBreakdown?.providers || [], type),
    [providerBreakdown?.providers, type]
  );

  const isNotCompleted = useCallback(() => {
    try {
      const unreceived = summary?.unreceived?.count || 0;
      const completedAt = data?.test?.completedAt;

      // If there's no completion date or all emails were received, report is complete
      if (!completedAt || unreceived === 0) return false;

      const completedDate = new Date(completedAt);
      if (isNaN(completedDate.getTime())) return false;

      const ageInMinutes = (Date.now() - completedDate.getTime()) / 60000;

      return unreceived > 0 && ageInMinutes < MAX_REPORT_AGE_MINUTES;
    } catch {
      return false;
    }
  }, [summary?.unreceived?.count, data?.test?.completedAt]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkReportAgeAndRefetch = () => {
      if (!isNotCompleted()) return;

      try {
        const refetchData = async () => {
          try {
            // Use Promise.all to fetch both data sources concurrently
            await Promise.all([
              refetchInboxPlacementReport(),
              refetchProviderBreakdown(),
            ]);

            const currentCount = SessionStorageManager.getRefetchCount(
              testId as string
            );
            SessionStorageManager.setRefetchCount(
              testId as string,
              currentCount + 1
            );
          } catch (error) {
            console.error("Error refetching data:", error);
          }
        };

        intervalId = setInterval(refetchData, REFETCH_INTERVAL_MS);
      } catch (error) {
        console.error("Error in checkReportAgeAndRefetch:", error);
      }
    };

    checkReportAgeAndRefetch();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    isNotCompleted,
    testId,
    refetchInboxPlacementReport,
    refetchProviderBreakdown,
  ]);

  if (
    !isLoadingInboxPlacementReport &&
    !providerBreakdownLoading &&
    !data?.success &&
    !providerBreakdown?.success
  ) {
    return (
      <div>
        <div className="text-center md:text-left">
          <div className=" flex items-center gap-3">
            <h2 className="text-2xl font-semibold">Test Results</h2>
          </div>

          <p className="text-muted-foreground text-sm md:text-base">
            {state.params.email}
          </p>
        </div>
        <StatusScreen
          title="No Inbox Placement Report Found"
          description="The inbox placement report you are looking for is not available. The report may not exist or the URL might be incorrect. Please check the URL and try again."
          icon={<InfoIcon />}
          iconWrapperClassName="text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
          variant="link-button"
          buttonText="Run a New Test"
          buttonLink={"/placement-test"}
          wrapperClass="min-h-[50vh] flex justify-center items-center w-full"
        />
      </div>
    );
  }

  return (
    <PlacementContext.Provider
      value={{
        state,
        setState,
        isNotCompleted,
        summary,
      }}
    >
      {children}
    </PlacementContext.Provider>
  );
};
